/**
 * WebSpeechSession — shared, robust wrapper around the Web Speech API
 * (`SpeechRecognition` / `webkitSpeechRecognition`).
 *
 * WHY THIS EXISTS
 * ───────────────
 * ~14 Timbrica tools each hand-rolled their own `recognition.onend` /
 * `onerror` handling. Quality ranged from "no error handling at all"
 * (voice-navigator, teleprompter, word-counter) to a sophisticated but
 * bespoke 3-layer restart (speech-to-text). The divergence caused real,
 * shipped bugs:
 *   - bug 3b5637cc — synchronous `recognition.start()` inside `onend`
 *     throws `InvalidStateError` on Yandex Browser → recording died at 14 s.
 *   - bug 24d8087b — `recognition.onerror` emitted ZERO telemetry on every
 *     branch, so `network` failures left `last_error:null` and the tool was
 *     un-investigable; the `network` branch also hard-stopped recording at
 *     ~1 s from a region with poor speech-backend routing.
 *
 * This module is the single эталонное implementation. It owns the
 * MECHANISM (restart strategy, error classification, telemetry, bounded
 * retry); the tool owns the POLICY response (what UI to show, whether to
 * fall back to Whisper) via callbacks.
 *
 * RESTART STRATEGY — combines the two best in-repo patterns:
 *   1. setTimeout escape (speech-to-text) — never call `start()` inside the
 *      synchronous `onend` tick; Yandex SpeechKit throws there.
 *   2. fresh-instance fallback (speech-to-text) — if `start()` still throws,
 *      rebuild the `SpeechRecognition` instance and retry once.
 *   3. time-windowed counter (live-captions) — a restart spaced > 2 s after
 *      the previous one means the session was productive between them, so
 *      the rapid-restart counter resets. Only genuinely rapid (tight-loop)
 *      restarts accumulate toward the cap. A healthy multi-hour dictation
 *      never trips it; a wedged engine trips it in ~1 s.
 *
 * ERROR POLICY:
 *   - `not-allowed`            → fatal 'permission'
 *                                → fatal 'backend' when the page demonstrably
 *                                  HAS the microphone (see below)
 *   - `service-not-allowed`    → fatal 'backend'    (mic OK, speech backend rejected)
 *   - `network` (1st)          → transient: one retry
 *   - `network` (persistent)   → fatal 'network'
 *   - `audio-capture`          → fatal 'audio-capture'
 *   - `language-not-supported` → fatal 'lang'
 *   - `no-speech` / `aborted`  → non-fatal, restart continues
 *   - rapid-restart cap hit    → fatal 'restart_cap'
 *   - start() fails twice      → fatal 'start_failed'
 *   - spoke, nothing delivered → fatal 'not_delivering'  (see below)
 * Every error — fatal or not — emits `feature_use:recognition_error`
 * telemetry. That universal trace is the single biggest reason this module
 * exists; it closes the diagnostic blind spot for every tool at once.
 *
 * DELIVERY IS NOT THE SAME QUESTION AS HEALTH (2026-09-03)
 * ───────────────────────────────────────────────────────
 * Everything above classifies what the engine SAYS. Nothing classified what
 * it DOES — and the dominant field failure says nothing at all. Measured on
 * /speech-to-text, 30 days, RU prod: of 4234 sessions that pressed record,
 * 1447 (34%) recorded audio and got ZERO text, and 1116 of those (77%) never
 * saw a single `recognition_error`. Reproduced on real desktop Chrome with
 * real Russian speech at a healthy mic level: `start` fires, `speechstart`
 * fires at 1.4 s — the engine admits it hears the audio — and then no result,
 * no error, no `no-speech`, forever. The tool sat in "Recording…" and the
 * user got an audio file and an empty transcript.
 *
 * A fatal-error-only fallback cannot reach that cohort, because there is no
 * error. So the module now also judges the lane by DELIVERY:
 *
 *   the user demonstrably spoke for DELIVERY_EVIDENCE_MS of accumulated
 *   speech time, and not one character of FINAL transcript has arrived
 *   → fatal 'not_delivering'
 *
 * Two properties make this safe to own here rather than per-tool:
 *   - it accumulates SPEECH time, not wall-clock, so a user who is simply
 *     quiet never trips it (the false-positive that voice-translator's
 *     bespoke wedge detector had to learn about the hard way: 17 of 73
 *     wedge verdicts there were patient users, not wedged engines);
 *   - it disarms permanently the moment any final text arrives, so a slow
 *     but working lane is never killed.
 *
 * ⚠️ Evidence must not come from the engine's own chatter. An interim result
 * is the engine talking about itself; a final transcript is the thing the
 * user came for. Counting any result as proof of life is exactly how the
 * empty-final / interim-only failures stayed invisible. Tools that own a
 * microphone stream should pass `speechEvidence` so the verdict rests on
 * OUR audio meter; without it the module falls back to the engine's
 * speechstart/speechend pair, which is weaker but still independent of
 * whether any transcript was produced.
 *
 * Success is now measured too: the first non-empty final of each run emits
 * `recognition_first_final` with its latency. Before this the module emitted
 * telemetry only on failure, so "did this tool ever work?" was unanswerable
 * for all 14 consumers at once — which is why the silent cohort above went
 * months without being noticed.
 *
 * `not-allowed` IS TWO DIFFERENT REFUSALS (2026-09-29)
 * ───────────────────────────────────────────────────
 * On a desktop it is the site's microphone permission. On Android, Chromium
 * does not recognise speech itself: it hands the audio to Google's speech
 * service (the Google app on Android ≤ 11, com.google.android.tts on 12+) and
 * maps THAT service's ERROR_INSUFFICIENT_PERMISSIONS and ERROR_RECOGNIZER_BUSY
 * onto the very same `not-allowed` (SpeechRecognitionImpl.java, onError).
 * So a page whose permission is granted — and whose own getUserMedia had just
 * opened the microphone — still heard `not-allowed`, and every consumer
 * answered "allow the microphone": a switch that was already on. Measured on
 * /voice-translator, RU, 05.07–29.09: 121 sessions from 101 IPs, 4–21 a week;
 * since 09.09 all 22 had opened the microphone first; not one of them ever
 * got a translation, one person pressed Start 14 times over two days.
 *
 * When the page demonstrably HAS the microphone — the tool's own `micReady()`
 * says it just captured it, or the Permissions API says 'granted' — the
 * refusal is the speech service's and is classified 'backend', the class that
 * already means "mic OK, speech service refused" to every consumer. `onFatal`
 * gets the evidence as its second argument, `{code, mic, service}`, so a tool
 * can name what to fix (`service: 'android'` — Google's recogniser on the
 * phone; 'apple' — Siri / speech recognition; 'browser' — the browser's own).
 * Without evidence `not-allowed` keeps its old meaning.
 *
 * USAGE
 * ─────
 *   var session = new WebSpeechSession({
 *       slug: 'speech-to-text',            // REQUIRED — telemetry attribution
 *       lang: 'en-US',                     // string
 *       continuous: true,                  // recognition.continuous
 *       interimResults: true,
 *       maxAlternatives: 1,
 *       autoRestart: true,                 // bool | function → restart on onend?
 *       onResult:      function (r) {},    // r = {final, interim, results, resultIndex, raw}
 *       onError:       function (e) {},    // e = {code, cls, fatal, willRetry}
 *       onStart:       function () {},
 *       onSpeechStart: function () {},
 *       onSpeechEnd:   function () {},
 *       onEnd:         function () {},     // session fully ended (NOT on auto-restart)
 *       onFatal:       function (reason, detail) {}, // unrecoverable — see ERROR POLICY;
 *                                          // detail = {code, mic, service}
 *       onRestart:     function (i) {},    // optional: {attempt, fresh}
 *       micReady:      function () {},    // optional: did THIS page just open
 *                                          // the microphone itself (its own
 *                                          // pre-flight resolved)? true → a
 *                                          // later `not-allowed` is the speech
 *                                          // service's refusal, not the site's.
 *       speechEvidence: function () {},    // optional: is the user speaking
 *                                          // RIGHT NOW, per the tool's own
 *                                          // audio meter? true / false /
 *                                          // null ("cannot tell" → the
 *                                          // module falls back to the
 *                                          // engine's speechstart/end).
 *   });
 *   session.start();
 *   session.stop();          // graceful — no auto-restart
 *   session.abort();         // immediate
 *   session.setLang('ru-RU');
 *   session.isActive;        // bool
 *   session.destroy();
 *   WebSpeechSession.isSupported();   // static
 *
 * No build step — plain ES5, attaches `window.WebSpeechSession`.
 */
(function (global) {
    'use strict';

    var SR = global.SpeechRecognition || global.webkitSpeechRecognition;

    /* ── Tunables ─────────────────────────────────────────────────────── */
    var RESTART_DELAY_MS   = 120;   /* escape the synchronous onend tick    */
    var HEALTHY_GAP_MS     = 2000;  /* restart spaced > this → reset counter */
    var MAX_RAPID_RESTARTS = 8;     /* consecutive tight-loop restarts → fatal */
    var NETWORK_RETRY_MAX  = 3;     /* transient network blips tolerated     */
                                    /* Raised 2026-05-22 from 1 → 3 because  */
                                    /* the on-result counter reset (see      */
                                    /* onresult below — historical hint)     */
                                    /* was removed: a chronically-poor       */
                                    /* connection that gets intermittent     */
                                    /* results between network errors no     */
                                    /* longer hides those errors. Prod       */
                                    /* telemetry (7d, 639 sessions):         */
                                    /* 8948 network recognition_error → only */
                                    /* 302 reached fatal → 337 sessions      */
                                    /* (53%) never got Whisper takeover and  */
                                    /* spun forever on broken backend.       */
    var FATAL_FINISH_MS    = 280;   /* fallback: force-finish if abort()'s   */
                                    /* onend never fires                     */
    /* Accumulated SPEECH time with zero final transcript before we call the
       lane dead. Not wall-clock: silence must never count. Measured on real
       desktop Chrome against real Russian speech — see the harvest in
       tests/playwright/web-speech-delivery-watchdog.mjs for the contract.
       Harvested on this machine against the looped Russian fixture: a lane
       that works delivers its first final at 3.4-4.0 s, so 12 s of SPEECH
       (longer still in wall-clock, since silence does not count) leaves ~3x
       margin. The same harvest is the scale of the problem — 17 trials on
       loud, clean speech produced text twice; the other 15 fired speechstart
       and then said nothing, which is the cohort this constant exists for. */
    var DELIVERY_EVIDENCE_MS = 12000;
    var DELIVERY_TICK_MS     = 250;

    function classify(code) {
        switch (code) {
            case 'not-allowed':            return 'permission';
            case 'service-not-allowed':    return 'backend';
            case 'network':                return 'network';
            case 'no-speech':              return 'no-speech';
            case 'audio-capture':          return 'audio-capture';
            case 'aborted':                return 'aborted';
            case 'language-not-supported': return 'lang';
            case 'bad-grammar':            return 'grammar';
            default:                       return 'unknown';
        }
    }

    /* Browser tag computed once per page — matches the `_b` convention used
       by session_start / rage_click so analytics can group recognition
       events by browser without a JOIN. Yandex matched BEFORE Chrome
       because Yandex Browser's UA contains both YaBrowser/ and Chrome/. */
    var _browserTag = (function () {
        try {
            var ua = (global.navigator && global.navigator.userAgent) || '';
            if (/YaBrowser\//i.test(ua))       return 'yandex';
            if (/Edg\//i.test(ua))             return 'edge';
            if (/OPR\/|Opera\//i.test(ua))     return 'opera';
            if (/SamsungBrowser\//i.test(ua))  return 'samsung';
            if (/Firefox\//i.test(ua))         return 'firefox';
            if (/Chrome\//i.test(ua))          return 'chrome';
            if (/Safari\//i.test(ua))          return 'safari';
            return 'other';
        } catch (_) { return 'other'; }
    })();

    /* Who recognises speech behind this browser — decides what a tool can
       tell the user to fix when the service refuses. Android: Google's speech
       service on the phone, for every Chromium browser there (Chrome, Yandex,
       Samsung). Apple: Siri / speech recognition. Otherwise the browser's own. */
    var _service = (function () {
        try {
            var nav = global.navigator || {};
            var ua = nav.userAgent || '';
            if (/Android/i.test(ua)) return 'android';
            if (/iPhone|iPad|iPod/i.test(ua)) return 'apple';
            if (/Macintosh/i.test(ua) && nav.maxTouchPoints > 1) return 'apple';   /* iPadOS */
            if (/Safari\//i.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR|YaBrowser|Firefox/i.test(ua)) return 'apple';
            return 'browser';
        } catch (_) { return 'browser'; }
    })();

    /* The page's microphone permission, kept current. Queried once when the
       module loads — long before anyone presses a button — so the answer is
       in hand by the time a `not-allowed` arrives; `change` keeps it fresh
       when the user grants the prompt a tool's pre-flight raised. null =
       unknown (no Permissions API, or it does not answer for 'microphone'). */
    var _micPerm = null;
    (function watchMicPermission() {
        try {
            var perms = global.navigator && global.navigator.permissions;
            if (!perms || typeof perms.query !== 'function') return;
            var q = perms.query({ name: 'microphone' });
            if (!q || typeof q.then !== 'function') return;
            q.then(function (st) {
                if (!st) return;
                var sync = function () { _micPerm = st.state || null; };
                sync();
                try { st.addEventListener('change', sync); }
                catch (_) { try { st.onchange = sync; } catch (__) {} }
            }, function () {});
        } catch (_) {}
    })();

    function emit(slug, f, extra) {
        try {
            if (!global.__ae) return;
            var p = { slug: slug, f: f, _b: _browserTag };
            if (extra) {
                for (var k in extra) {
                    if (extra.hasOwnProperty(k) && extra[k] !== undefined && extra[k] !== null) {
                        p[k] = extra[k];
                    }
                }
            }
            global.__ae('feature_use', p);
        } catch (_) {}
    }

    function noop() {}

    function WebSpeechSession(opts) {
        opts = opts || {};
        if (!opts.slug) {
            throw new Error('WebSpeechSession: opts.slug is required (telemetry attribution)');
        }
        this.slug          = opts.slug;
        this._lang         = opts.lang || '';
        this._continuous   = opts.continuous !== false;      /* default true  */
        this._interim      = opts.interimResults !== false;  /* default true  */
        this._maxAlt       = opts.maxAlternatives || 1;
        this._autoRestart  = (opts.autoRestart === undefined) ? true : opts.autoRestart;

        this.onResult      = opts.onResult      || noop;
        this.onError       = opts.onError       || noop;
        this.onStart       = opts.onStart       || noop;
        this.onSpeechStart = opts.onSpeechStart || noop;
        this.onSpeechEnd   = opts.onSpeechEnd   || noop;
        this.onEnd         = opts.onEnd         || noop;
        this.onFatal       = opts.onFatal       || noop;
        this.onRestart     = opts.onRestart     || noop;
        /* Optional: the tool's OWN answer to "is the user speaking right
           now". Independent of the recogniser, so a lane that has gone quiet
           cannot vouch for itself. */
        this.speechEvidence = (typeof opts.speechEvidence === 'function')
            ? opts.speechEvidence : null;
        /* Optional: the tool's own proof that it just opened the microphone —
           stronger than the Permissions API, which not every engine answers
           for 'microphone'. See "`not-allowed` IS TWO DIFFERENT REFUSALS". */
        this.micReady = (typeof opts.micReady === 'function') ? opts.micReady : null;

        /* ── lifecycle state ──────────────────────────────────────────── */
        this.isActive       = false;  /* the session is supposed to be capturing */
        this._rec           = null;
        this._stopping      = false;  /* graceful stop / abort in progress  */
        this._fatal         = false;  /* unrecoverable — no more restarts   */
        this._finished      = false;  /* onEnd already delivered this run   */
        this._rapidCount    = 0;
        this._lastRestartAt = 0;
        this._networkErrs   = 0;
        this._netOverrideUsed = false;
        this._restartTimer  = null;
        this._finishTimer   = null;
        this._destroyed     = false;
        /* ── delivery watchdog (per run) ───────────────────────────────── */
        this._deliveryTimer = null;
        this._evidenceMs    = 0;      /* accumulated speech time this run   */
        this._finalChars    = 0;      /* non-empty FINAL chars this run     */
        this._engineHearing = false;  /* between speechstart and speechend  */
        this._evidenceSrc   = '';     /* which source actually answered     */
        this._startedAt     = 0;
    }

    WebSpeechSession.isSupported = function () { return !!SR; };

    /* Build a fresh SpeechRecognition instance with all handlers wired. */
    WebSpeechSession.prototype._build = function () {
        var self = this;
        var rec = new SR();
        rec.continuous      = this._continuous;
        rec.interimResults  = this._interim;
        rec.maxAlternatives = this._maxAlt;
        if (this._lang) { try { rec.lang = this._lang; } catch (_) {} }

        rec.onstart = function () {
            if (self._destroyed) return;
            try { self.onStart(); } catch (_) {}
        };
        rec.onspeechstart = function () {
            if (self._destroyed) return;
            self._engineHearing = true;
            try { self.onSpeechStart(); } catch (_) {}
        };
        rec.onspeechend = function () {
            if (self._destroyed) return;
            self._engineHearing = false;
            try { self.onSpeechEnd(); } catch (_) {}
        };
        rec.onresult = function (e) {
            if (self._destroyed) return;
            /* Reset the one-shot net-override flag so a later blip can use
               it again — but DELIBERATELY DO NOT reset _networkErrs.
               History (2026-05-22): the original code reset both on every
               result; in regions with a flaky speech backend (RU+lossy
               link), interleaved result→network-error→result→network-error
               cycles kept _networkErrs at 0 forever, so it never crossed
               NETWORK_RETRY_MAX → never went fatal → Whisper takeover
               never engaged. Prod evidence: 8948 net errors / 639 sessions
               but only 302 fatal/takeover. The counter is now a LIFETIME
               tally; only the one-shot single-retry slot resets. */
            self._netOverrideUsed = false;
            var interim = '', final = '';
            var startIdx = (typeof e.resultIndex === 'number') ? e.resultIndex : 0;
            for (var i = startIdx; i < e.results.length; i++) {
                var r = e.results[i];
                if (!r || !r[0] || typeof r[0].transcript !== 'string') continue;
                if (r.isFinal) final += r[0].transcript;
                else interim += r[0].transcript;
            }
            if (final) self._rapidCount = 0;
            /* DELIVERY — only a non-empty FINAL counts. An interim is the
               engine talking about itself; the user came for the transcript.
               The first one disarms the watchdog for the rest of the run and
               is the module's one success measurement. */
            if (final && final.trim()) {
                if (self._finalChars === 0) {
                    emit(self.slug, 'recognition_first_final', {
                        ms: Date.now() - self._startedAt,
                        ev_ms: Math.round(self._evidenceMs),
                    });
                }
                self._finalChars += final.trim().length;
                self._stopDeliveryWatch();
            }
            try {
                self.onResult({
                    final: final, interim: interim,
                    results: e.results, resultIndex: startIdx, raw: e,
                });
            } catch (_) {}
        };
        rec.onerror = function (e) {
            if (self._destroyed) return;
            self._handleError(e && e.error ? e.error : 'unknown');
        };
        rec.onend = function () {
            if (self._destroyed) return;
            self._handleEnd();
        };
        this._rec = rec;
        return rec;
    };

    /* ── Delivery watchdog ────────────────────────────────────────────
       Ticks while the run is active, accumulating only the time the user is
       actually speaking. Evidence is the tool's own meter when it supplied
       one, else the engine's speechstart/speechend pair — never the arrival
       of results, which is the very thing being judged. */
    WebSpeechSession.prototype._startDeliveryWatch = function () {
        var self = this;
        this._stopDeliveryWatch();
        if (DELIVERY_EVIDENCE_MS <= 0) return;
        this._deliveryTimer = setInterval(function () {
            if (self._destroyed || self._fatal || !self.isActive) {
                self._stopDeliveryWatch();
                return;
            }
            /* Three-valued on purpose. `null` means the tool's meter cannot
               answer right now — its AudioContext was closed out from under
               it, the stream is not open yet — and that must degrade to the
               engine's own speechstart rather than to "no evidence", or the
               watchdog silently disarms exactly where it is needed most.
               (The reporter's session that opened this investigation had a
               CLOSED AudioContext throughout: a meter-only rule would have
               accumulated zero evidence and never fired.) */
            var speaking, src;
            var ev = null;
            if (self.speechEvidence) {
                try { ev = self.speechEvidence(); } catch (_) { ev = null; }
            }
            if (ev === null || ev === undefined) { speaking = self._engineHearing; src = 'engine'; }
            else { speaking = !!ev; src = 'meter'; }
            self._evidenceSrc = src;
            if (!speaking) return;
            self._evidenceMs += DELIVERY_TICK_MS;
            if (self._evidenceMs < DELIVERY_EVIDENCE_MS || self._finalChars > 0) return;
            self._stopDeliveryWatch();
            emit(self.slug, 'recognition_not_delivering', {
                ev_ms: Math.round(self._evidenceMs),
                run_ms: Date.now() - self._startedAt,
                src: self._evidenceSrc || 'engine',
                lang: self._lang || '',
            });
            self._goFatal('not_delivering');
        }, DELIVERY_TICK_MS);
    };

    WebSpeechSession.prototype._stopDeliveryWatch = function () {
        if (this._deliveryTimer) { clearInterval(this._deliveryTimer); this._deliveryTimer = null; }
    };

    WebSpeechSession.prototype._evalAutoRestart = function () {
        var a = this._autoRestart;
        if (typeof a === 'function') {
            try { return !!a(); } catch (_) { return false; }
        }
        return !!a;
    };

    /* Evidence that the page itself has the microphone: 'capture' — the
       tool's own pre-flight just opened it; 'permission' — the page's
       microphone permission is granted; null — no evidence. */
    WebSpeechSession.prototype._micEvidence = function () {
        if (this.micReady) {
            var v = null;
            try { v = this.micReady(); } catch (_) { v = null; }
            if (v === true) return 'capture';
        }
        return _micPerm === 'granted' ? 'permission' : null;
    };

    WebSpeechSession.prototype._handleError = function (code) {
        var cls = classify(code);

        /* `aborted` from our own stop()/abort()/fatal path is not a fault —
           don't pollute telemetry or callbacks with it. */
        if (cls === 'aborted' && (this._stopping || this._fatal)) return;

        /* A page that HAS the microphone was not refused it by the site's
           permission — the speech service behind the browser refused. See
           "`not-allowed` IS TWO DIFFERENT REFUSALS" in the header. */
        var mic = null;
        if (cls === 'permission') {
            mic = this._micEvidence();
            if (mic) cls = 'backend';
        }

        /* Universal telemetry — emitted on EVERY branch. This is the single
           most important line in the module: it closes the diagnostic blind
           spot that made bug 24d8087b un-investigable. */
        emit(this.slug, 'recognition_error', {
            error: code, cls: cls, mic: mic, svc: mic ? _service : null,
        });

        var fatalReason = null;
        if (cls === 'network') {
            this._networkErrs++;
            /* First blip = transient (let onend retry); persistent = fatal. */
            if (this._networkErrs > NETWORK_RETRY_MAX) fatalReason = 'network';
        } else if (cls === 'permission') {
            fatalReason = 'permission';
        } else if (cls === 'backend') {
            fatalReason = 'backend';
        } else if (cls === 'audio-capture') {
            fatalReason = 'audio-capture';
        } else if (cls === 'lang') {
            fatalReason = 'lang';
        }
        /* no-speech / grammar / unknown → non-fatal; onend decides restart. */

        try {
            this.onError({
                code: code, cls: cls, mic: mic,
                fatal: !!fatalReason,
                willRetry: !fatalReason && this.isActive,
            });
        } catch (_) {}

        if (fatalReason) this._goFatal(fatalReason, { code: code, mic: mic });
    };

    WebSpeechSession.prototype._handleEnd = function () {
        /* Graceful stop, fatal, or no longer wanted → the session is over. */
        if (this._stopping || this._fatal || !this.isActive) {
            this._finish();
            return;
        }
        if (this._evalAutoRestart()) {
            this._scheduleRestart();
            return;
        }
        /* Auto-restart is off. Exception: a single transient network error
           must still get ONE retry — a blip should not end the session for
           a non-continuous user either (bug 24d8087b: death at ~1 s). The
           override is consumed exactly once; a productive result re-arms it. */
        if (this._networkErrs >= 1 && this._networkErrs <= NETWORK_RETRY_MAX && !this._netOverrideUsed) {
            this._netOverrideUsed = true;
            this._scheduleRestart();
            return;
        }
        this._finish();
    };

    WebSpeechSession.prototype._scheduleRestart = function () {
        var self = this;
        var now = Date.now();
        /* A restart spaced well after the previous one means the session
           ran productively between them — not a tight loop. Reset the count. */
        if (this._lastRestartAt && (now - this._lastRestartAt) > HEALTHY_GAP_MS) {
            this._rapidCount = 0;
        }
        this._rapidCount++;
        if (this._rapidCount > MAX_RAPID_RESTARTS) {
            this._goFatal('restart_cap');
            return;
        }
        var attempt = this._rapidCount;
        this._lastRestartAt = now;
        if (this._restartTimer) clearTimeout(this._restartTimer);
        this._restartTimer = setTimeout(function () {
            self._restartTimer = null;
            if (self._destroyed || self._stopping || self._fatal || !self.isActive) return;
            self._doRestart(attempt);
        }, RESTART_DELAY_MS);
    };

    WebSpeechSession.prototype._doRestart = function (attempt) {
        try {
            this._rec.start();
            if (attempt >= 2) emit(this.slug, 'recognition_restart', { attempt: attempt, fresh: 0 });
            try { this.onRestart({ attempt: attempt, fresh: false }); } catch (_) {}
        } catch (e1) {
            /* Same-instance restart refused — Yandex SpeechKit locks an
               instance in a "stopping" state. Rebuild and try once more. */
            try {
                this._build();
                this._rec.start();
                emit(this.slug, 'recognition_restart', {
                    attempt: attempt, fresh: 1, err: e1 && e1.name,
                });
                try { this.onRestart({ attempt: attempt, fresh: true }); } catch (_) {}
            } catch (e2) {
                emit(this.slug, 'recognition_restart_failed', {
                    attempt: attempt, err1: e1 && e1.name, err2: e2 && e2.name,
                });
                this._goFatal('start_failed');
            }
        }
    };

    WebSpeechSession.prototype._goFatal = function (reason, why) {
        if (this._fatal) return;            /* idempotent */
        this._fatal = true;
        if (this._restartTimer) { clearTimeout(this._restartTimer); this._restartTimer = null; }
        this._stopDeliveryWatch();
        /* `code` — the engine's own word when the fatal came from one
           (`not-allowed` behind a 'backend' is the case that needs it);
           `mic` — why the module believes the page has the microphone. */
        var detail = {
            code: (why && why.code) || null,
            mic: (why && why.mic) || null,
            service: _service,
        };
        emit(this.slug, 'recognition_fatal', { reason: reason, code: detail.mic ? detail.code : null, mic: detail.mic });
        try { this.onFatal(reason, detail); } catch (_) {}
        /* Abort the dead recognition; its onend runs _finish. A fallback
           timer covers engines that never fire onend after abort(). */
        var self = this;
        var hadRec = !!this._rec;
        if (hadRec) { try { this._rec.abort(); } catch (_) {} }
        if (this._finishTimer) clearTimeout(this._finishTimer);
        this._finishTimer = setTimeout(function () { self._finish(); },
            hadRec ? FATAL_FINISH_MS : 0);
    };

    WebSpeechSession.prototype._finish = function () {
        if (this._finished) return;          /* once per run */
        if (this._restartTimer) { clearTimeout(this._restartTimer); this._restartTimer = null; }
        if (this._finishTimer)  { clearTimeout(this._finishTimer);  this._finishTimer  = null; }
        this._stopDeliveryWatch();
        this.isActive  = false;
        this._stopping = false;
        /* destroy() means "tear down, no more callbacks" — a _finish already
           scheduled by _goFatal/abort must not deliver a late onEnd. */
        if (this._destroyed) return;
        this._finished = true;
        try { this.onEnd(); } catch (_) {}
    };

    /* ── public API ───────────────────────────────────────────────────── */

    WebSpeechSession.prototype.start = function () {
        if (this._destroyed) return false;
        if (!SR) {
            try {
                this.onError({ code: 'not-supported', cls: 'unsupported', fatal: true, willRetry: false });
            } catch (_) {}
            try { this.onFatal('not-supported'); } catch (_) {}
            return false;
        }
        if (this.isActive) return true;      /* already running — idempotent */
        this.isActive        = true;
        this._stopping       = false;
        this._fatal          = false;
        this._finished       = false;
        this._rapidCount     = 0;
        this._lastRestartAt  = 0;
        this._networkErrs    = 0;
        this._netOverrideUsed = false;
        this._evidenceMs     = 0;
        this._finalChars     = 0;
        this._engineHearing  = false;
        this._evidenceSrc    = '';
        this._startedAt      = Date.now();
        /* _build() is INSIDE the try: `new SR()` itself can throw when the
           engine is disabled by enterprise policy / platform speech service
           (measured on Edge 2026-07-30). Before this, the exception escaped
           start() and left callers' UI dead mid-transition with zero
           telemetry. Now both construction and .start() failures resolve to
           recognition_start_failed + fatal — callers get a clean `false`. */
        try {
            this._build();
            this._rec.start();
        } catch (e1) {
            /* A wedged or already-started instance — one fresh-instance retry. */
            try {
                this._build();
                this._rec.start();
            } catch (e2) {
                emit(this.slug, 'recognition_start_failed', {
                    err1: e1 && e1.name, err2: e2 && e2.name,
                });
                this._goFatal('start_failed');
                return false;
            }
        }
        this._startDeliveryWatch();
        return true;
    };

    /* Graceful stop — finishes the current utterance, no auto-restart. */
    WebSpeechSession.prototype.stop = function () {
        if (!this.isActive || this._stopping || this._fatal) return;
        this._stopping = true;
        if (this._restartTimer) { clearTimeout(this._restartTimer); this._restartTimer = null; }
        if (this._rec) {
            try { this._rec.stop(); } catch (_) {}
        } else {
            this._finish();
        }
        /* Fallback: some engines never fire onend after stop(). */
        var self = this;
        if (this._finishTimer) clearTimeout(this._finishTimer);
        this._finishTimer = setTimeout(function () { self._finish(); }, 1500);
    };

    /* Immediate stop — drops the in-flight utterance. */
    WebSpeechSession.prototype.abort = function () {
        if (!this.isActive || this._fatal) { return; }
        this._stopping = true;
        if (this._restartTimer) { clearTimeout(this._restartTimer); this._restartTimer = null; }
        if (this._rec) {
            try { this._rec.abort(); } catch (_) {}
        }
        var self = this;
        if (this._finishTimer) clearTimeout(this._finishTimer);
        this._finishTimer = setTimeout(function () { self._finish(); }, FATAL_FINISH_MS);
    };

    WebSpeechSession.prototype.setLang = function (lang) {
        this._lang = lang || '';
        if (this._rec) { try { this._rec.lang = this._lang; } catch (_) {} }
    };

    WebSpeechSession.prototype.setAutoRestart = function (v) {
        this._autoRestart = v;
    };

    WebSpeechSession.prototype.destroy = function () {
        this._destroyed = true;
        if (this._restartTimer) { clearTimeout(this._restartTimer); this._restartTimer = null; }
        if (this._finishTimer)  { clearTimeout(this._finishTimer);  this._finishTimer  = null; }
        this._stopDeliveryWatch();
        if (this._rec) {
            try { this._rec.abort(); } catch (_) {}
            this._rec = null;
        }
        this.isActive = false;
    };

    global.WebSpeechSession = WebSpeechSession;
})(typeof window !== 'undefined' ? window : this);
